/**
 * @fileoverview Footer — kurumsal sütunlar + alt çubuk.
 * Metinler: siteLayout (CMS); çeviri anahtarları: common (nav.*, footer.* etiketleri).
 */

import { createElement }  from 'react'
import { Link }           from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn }             from '../../utils/cn'
import {
  DEFAULT_FOOTER_SOCIAL,
  DEFAULT_FOOTER_MAIN_NAV_LINKS,
} from '../../features/siteLayout/defaultNavLinks'
import {
  FOOTER_SOCIAL_ICON_FALLBACK_ORDER,
  resolveFooterSocialIconComponent,
} from '../../features/siteLayout/footerSocialIcons'
import {
  normalizeFooterContactRows,
  resolveFooterContactHref,
} from '../../features/siteLayout/footerContactRows'

function isSocialRowActive(raw) {
  if (raw === false || raw === 0 || raw === '0') return false
  return true
}

/** @param {{ to: string, label: string, className?: string }} */
function FooterLink({ to, label, className }) {
  const isExternal = to?.startsWith('http') || to === '#'
  const base = cn(
    'text-sm text-white/75 hover:text-[#BF953F] transition-colors duration-200',
    className
  )
  if (isExternal) return <a href={to} target="_blank" rel="noopener noreferrer" className={base}>{label}</a>
  return <Link to={to} className={base}>{label}</Link>
}

export default function Footer() {
  const { t, i18n } = useTranslation('common')
  const { t: ts } = useTranslation('siteLayout')
  const year  = new Date().getFullYear()

  const lang = (i18n.language || 'tr').split('-')[0]
  const bundle = i18n.getResourceBundle(lang, 'siteLayout')
  const f = bundle?.footer && typeof bundle.footer === 'object' ? bundle.footer : {}

  const line = (key) => ts(`footer.${key}`, { defaultValue: t(`footer.${key}`) })

  const socialSource =
    Array.isArray(f.social) && f.social.length > 0 ? f.social : DEFAULT_FOOTER_SOCIAL

  const socialList = socialSource
    .filter((s) => isSocialRowActive(s?.active))
    .map((s, i) => {
      const href = String(s?.href ?? '')
      const label = String(s?.label ?? '')
      const iconId =
        s?.icon != null && String(s?.icon ?? '').trim() !== ''
          ? s.icon
          : FOOTER_SOCIAL_ICON_FALLBACK_ORDER[i % FOOTER_SOCIAL_ICON_FALLBACK_ORDER.length]
      return {
        href,
        label,
        Icon: resolveFooterSocialIconComponent(iconId),
      }
    })

  const mainNav =
    Array.isArray(f.mainNavLinks) && f.mainNavLinks.length > 0 ? f.mainNavLinks : DEFAULT_FOOTER_MAIN_NAV_LINKS

  const contactRows = normalizeFooterContactRows(f.contactRows, f)

  return (
    <footer className="bg-[#0D172A] border-t border-white/5 contain-[layout]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:items-start">

        <div className="md:col-span-5 space-y-6">
          <Link
            to="/"
            className="inline-block text-xl font-black tracking-tight"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              background: 'linear-gradient(135deg, #BF953F, #FCF6BA, #AA771C)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {ts('header.brandName', { defaultValue: 'GlobalAnka' })}
          </Link>

          <p
            className="text-white/75 text-sm leading-relaxed max-w-xs line-clamp-4 min-h-[5.6875rem]"
            style={{ fontFamily: '"DM Sans", system-ui, sans-serif' }}
          >
            {line('tagline')}
          </p>

          <div className="flex min-h-10 flex-wrap gap-3">
            {socialList.map(({ href, label, Icon }, i) => (
              <a
                key={`footer-social-${i}-${href}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center text-white/75 hover:text-[#BF953F] hover:border-[#BF953F]/50 transition-all duration-300"
              >
                {createElement(Icon, { className: 'w-4 h-4' })}
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#BF953F] font-bold mb-5">
            {line('navTitle')}
          </h3>
          <ul className="space-y-3">
            {mainNav.map(({ to, labelKey }) => (
              <li key={`${to}-${labelKey}`}>
                <FooterLink to={to} label={t(labelKey)} />
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 md:min-h-48">
          <h3 className="text-[10px] uppercase tracking-[0.3em] text-[#BF953F] font-bold mb-5">
            {line('contact')}
          </h3>
          <address className="not-italic space-y-4 text-sm text-white/75">
            {contactRows.map((row, idx) => {
              const title = String(row.title ?? '').trim()
              const body = String(row.body ?? '').trim()
              const hrefRaw = String(row.href ?? '').trim()
              if (!title && !body) return null
              const hrefResolved = hrefRaw ? resolveFooterContactHref(hrefRaw) : ''
              return (
                <div key={`fc-${idx}`} className="space-y-1">
                  {title ? (
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#BF953F]/90 font-semibold">{title}</p>
                  ) : null}
                  {hrefResolved ? (
                    hrefResolved.startsWith('/') && !hrefResolved.startsWith('//') ? (
                      <Link
                        to={hrefResolved}
                        className="block whitespace-pre-line hover:text-[#BF953F] transition-colors"
                      >
                        {body || hrefRaw}
                      </Link>
                    ) : (
                      <a
                        href={hrefResolved}
                        className="block whitespace-pre-line hover:text-[#BF953F] transition-colors"
                        {...(hrefResolved.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {body || hrefRaw}
                      </a>
                    )
                  ) : (
                    <p className="whitespace-pre-line">{body}</p>
                  )}
                </div>
              )
            })}
          </address>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[11px] text-white/65">
            © {year} {line('company')}. {line('rights')}
          </p>
          <p className="text-[11px] text-white/60 tracking-wider uppercase">
            Designed with Architectural Standards
          </p>
        </div>
      </div>
    </footer>
  )
}
